// ============================================================
// FEATURE: PatchBay — a cross-window POWER ROUTER / switchboard.
//
// This is a SENDER. It owns nothing visual that proves anything on
// its own: its whole point is that throwing a switch HERE lights a
// rail in a SEPARATE window (the PowerMonitor program). Open the
// Annex in two browser windows / tabs, put a PatchBay in one and a
// PowerMonitor in the other, and every throw feeds power across the
// gap live.
//
// Controls:
//   * a MASTER breaker (big throw) — cuts/feeds the whole bus
//   * six numbered RAIL switches — each routes power to one rail
//   * a SURGE button — fires a one-shot pulse down the live rails
//
// All state is pushed onto the shared windowBus under three keys:
//   power.master  : boolean        (master breaker engaged)
//   power.rails   : boolean[6]      (per-rail switch state)
//   power.surge   : { t, rails }    (one-shot surge stamp)
//
// windowBus carries these to EVERY other open window via localStorage
// + the "storage" event, so the receiver reacts with no shared parent.
//
// Non-draggable content component — mount as the artComp of an
// AnnexWindow to inherit the draggable titlebar + minimize.
// ============================================================

import * as React from "react";
import "./patchbay.css";
import windowBus from "../_shared/windowBus";
import { tactile, payoff } from "../_shared/feedback";

const RAIL_COUNT = 6;

// Stable labels for the six rails — dull, industrial, faintly clinical.
const RAIL_LABELS = [
  "DORSAL",
  "LATERAL",
  "INTAKE",
  "AMBULATORY",
  "SPINE",
  "EFFLUENT",
];

class PatchBay extends React.Component {
  constructor(props) {
    super(props);

    // Seed from any state a prior / sibling window already published, so a
    // freshly-mounted PatchBay agrees with the rest of the system instead of
    // resetting it. Falls back to a sane default (master on, all rails off).
    const seededMaster = windowBus.getState("power.master");
    const seededRails = windowBus.getState("power.rails");

    this.state = {
      master: typeof seededMaster === "boolean" ? seededMaster : true,
      rails: Array.isArray(seededRails)
        ? normalizeRails(seededRails)
        : new Array(RAIL_COUNT).fill(false),
    };

    this.toggleMaster = this.toggleMaster.bind(this);
    this.toggleRail = this.toggleRail.bind(this);
    this.fireSurge = this.fireSurge.bind(this);
  }

  componentDidMount() {
    // Publish current state once on mount so a receiver that was already open
    // (or opens later) is brought in sync with this board.
    windowBus.publish("power.master", this.state.master);
    windowBus.publish("power.rails", this.state.rails);
  }

  // The board is a physical-feeling switchboard, so every throw answers back
  // with a click/buzz. props.sound is threaded from the Annex's global mute;
  // when false the audio blip is skipped but the haptic may still fire.
  _feedback(kind) {
    tactile({ kind, sound: this.props.sound !== false });
  }

  toggleMaster() {
    // The big breaker reads as a firm switch throw.
    this._feedback("switch");
    this.setState(
      (s) => ({ master: !s.master }),
      () => windowBus.publish("power.master", this.state.master)
    );
  }

  toggleRail(i) {
    // Each rail switch gives the same crisp throw under the finger.
    this._feedback("switch");
    this.setState(
      (s) => {
        const rails = s.rails.slice();
        rails[i] = !rails[i];
        return { rails };
      },
      () => windowBus.publish("power.rails", this.state.rails)
    );
  }

  fireSurge() {
    // A surge only means anything when the master is engaged; fire it down
    // whichever rails are currently live. The stamp's timestamp makes each
    // press distinct so the receiver always sees a fresh event.
    if (!this.state.master) return;

    // This is an ACTIVATION — a real payoff, not just a click. Under the finger
    // it answers with the bigger rolling haptic + layered discharge burst.
    payoff({ kind: "surge", sound: this.props.sound !== false });

    const liveCount = this.state.rails.filter(Boolean).length;

    // Keep the existing one-shot surge mechanic (lamp flare on the monitor).
    windowBus.publish("power.surge", {
      t: Date.now(),
      rails: this.state.rails.slice(),
    });

    // Broadcast a SEPARATE, dramatic "overload" event across every window. The
    // PowerMonitor (and any listener) erupts on this: screen-flash, meters
    // slamming to max + pinning, a shockwave, readouts spiking then settling.
    // intensity (0..1) scales with how many rails are carrying the discharge.
    windowBus.publish("power.overload", {
      t: Date.now(),
      source: "surge",
      intensity: clamp01(liveCount / RAIL_COUNT) * 0.6 + 0.4,
    });
  }

  render() {
    const { master, rails } = this.state;
    const liveCount = master ? rails.filter(Boolean).length : 0;

    return (
      <div className={"pb-rig" + (master ? " pb-live" : " pb-dead")}>
        <div className="pb-readout">
          <span className="pb-readout-label">BUS FEED</span>
          <span className="pb-readout-val">
            {master ? "ARMED" : "OPEN"} &middot; {liveCount}/{RAIL_COUNT}
          </span>
        </div>

        {/* MASTER breaker — a big throw on the left */}
        <div className="pb-master-wrap">
          <button
            type="button"
            className={"pb-master" + (master ? " pb-master-on" : "")}
            onClick={this.toggleMaster}
            aria-pressed={master}
            title="Master breaker"
          >
            <span className="pb-master-throw" aria-hidden="true" />
            <span className="pb-master-cap">{master ? "FEED" : "CUT"}</span>
          </button>
          <span className="pb-master-tag">MASTER</span>
        </div>

        {/* The six rail switches */}
        <div className="pb-rails">
          {rails.map((on, i) => {
            const live = master && on;
            return (
              <button
                key={i}
                type="button"
                className={
                  "pb-rail" +
                  (on ? " pb-rail-thrown" : "") +
                  (live ? " pb-rail-live" : "")
                }
                onClick={() => this.toggleRail(i)}
                aria-pressed={on}
                title={"Rail " + (i + 1) + " — " + RAIL_LABELS[i]}
              >
                <span className="pb-rail-track" aria-hidden="true">
                  <span className="pb-rail-knob" />
                </span>
                <span className="pb-rail-num">{i + 1}</span>
                <span className="pb-rail-name">{RAIL_LABELS[i]}</span>
                <span className="pb-rail-lamp" aria-hidden="true" />
              </button>
            );
          })}
        </div>

        {/* SURGE — fires a one-shot pulse down the live rails */}
        <button
          type="button"
          className="pb-surge"
          onClick={this.fireSurge}
          disabled={!master}
          title="Send a surge down the live rails"
        >
          SURGE ↯
        </button>

        <div className="pb-foot">
          ROUTES TO A SEPARATE <b>POWER-MONITOR</b> WINDOW
        </div>
      </div>
    );
  }
}

// Keep a number inside 0..1, defaulting bad input to 0. Used to scale the
// overload intensity broadcast.
function clamp01(n) {
  if (typeof n !== "number" || !isFinite(n)) return 0;
  if (n <= 0) return 0;
  if (n >= 1) return 1;
  return n;
}

// Coerce whatever arrives off the bus into a clean boolean[RAIL_COUNT].
function normalizeRails(arr) {
  const out = new Array(RAIL_COUNT).fill(false);
  for (let i = 0; i < RAIL_COUNT; i++) out[i] = !!arr[i];
  return out;
}

export default PatchBay;
